import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { queryKeys } from '@/utils/queryKeys';
import type {
  AssignReferralRequest,
  CommissionDto,
  CreateCommentRequest,
  CreateReferralRequest,
  PagedResponse,
  ReferralActivityDto,
  ReferralCommentDto,
  ReferralDto,
  ReferralMetricsDto,
  UpdateAdminNotesRequest,
  UpdateCommissionPayoutStatusRequest,
  UpdateReferralStatusRequest,
  UpdateReferrerNotesRequest,
} from '@/types/referral';
import type { ReferralStatus } from '@/config/services';

// ─── Query param interfaces ──────────────────────────────────────────────────

export interface ReferralListParams {
  page?: number;
  pageSize?: number;
  status?: ReferralStatus | '';
  location?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
}

export interface MyReferralsParams {
  page?: number;
  pageSize?: number;
  status?: ReferralStatus | '';
}

// ─── Admin list ──────────────────────────────────────────────────────────────

export function useReferrals(params: ReferralListParams = {}) {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    ...(params.status ? { status: params.status } : {}),
    ...(params.location ? { location: params.location } : {}),
    ...(params.search ? { search: params.search } : {}),
    ...(params.fromDate ? { fromDate: params.fromDate } : {}),
    ...(params.toDate ? { toDate: params.toDate } : {}),
  };

  return useQuery({
    queryKey: queryKeys.referrals.list(params as Record<string, unknown>),
    queryFn: () => apiClient.get<PagedResponse<ReferralDto>>('/api/referrals', queryParams),
  });
}

// ─── My Referrals (by email) ─────────────────────────────────────────────────

export function useMyReferrals(email: string, params: MyReferralsParams = {}) {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    ...(params.status ? { status: params.status } : {}),
  };

  return useQuery({
    queryKey: queryKeys.referrals.mine(email, params as Record<string, unknown>),
    queryFn: () =>
      apiClient.get<PagedResponse<ReferralDto>>('/api/referrals/mine', {
        ...queryParams,
        email,
      }),
    enabled: !!email,
  });
}

// ─── Create referral ─────────────────────────────────────────────────────────

export function useCreateReferral() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReferralRequest) =>
      apiClient.post<ReferralDto>('/api/referrals', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.all });
    },
  });
}

// ─── Delete referral ────────────────────────────────────────────────────────

export function useDeleteReferral() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      performedBy,
      performedByName,
    }: {
      id: number;
      performedBy: string;
      performedByName: string;
    }) => {
      const params = new URLSearchParams({ performedBy, performedByName });
      return apiClient.delete(`/api/referrals/${id}?${params.toString()}`);
    },
    onSuccess: (_data, { id }) => {
      qc.removeQueries({ queryKey: queryKeys.referrals.detail(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.all });
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.metrics });
    },
  });
}

// ─── Update referrer notes ───────────────────────────────────────────────────

export function useUpdateReferrerNotes() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, email, data }: { id: number; email: string; data: UpdateReferrerNotesRequest }) =>
      apiClient.put<ReferralDto>(`/api/referrals/${id}/notes?email=${encodeURIComponent(email)}`, data),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.referrals.detail(updated.id), updated);
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.all });
    },
  });
}

// ─── Update referral status ──────────────────────────────────────────────────

export function useUpdateReferralStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
      performedBy,
      performedByName,
    }: {
      id: number;
      data: UpdateReferralStatusRequest;
      performedBy?: string;
      performedByName?: string;
    }) => {
      const params = new URLSearchParams();
      if (performedBy) params.set('performedBy', performedBy);
      if (performedByName) params.set('performedByName', performedByName);
      const qs = params.toString();
      return apiClient.put<ReferralDto>(
        `/api/referrals/${id}/status${qs ? `?${qs}` : ''}`,
        data,
      );
    },
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.referrals.detail(updated.id), updated);
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.all });
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.activities(updated.id) });
    },
  });
}

// ─── Referral activities ────────────────────────────────────────────────────

export function useReferralActivities(id: number) {
  return useQuery({
    queryKey: queryKeys.referrals.activities(id),
    queryFn: () => apiClient.get<ReferralActivityDto[]>(`/api/referrals/${id}/activities`),
    enabled: id > 0,
  });
}

// ─── Update admin notes ─────────────────────────────────────────────────────

export function useUpdateAdminNotes() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
      performedBy,
      performedByName,
    }: {
      id: number;
      data: UpdateAdminNotesRequest;
      performedBy: string;
      performedByName: string;
    }) => {
      const params = new URLSearchParams({ performedBy, performedByName });
      return apiClient.put<ReferralDto>(
        `/api/referrals/${id}/admin-notes?${params.toString()}`,
        data,
      );
    },
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.referrals.detail(updated.id), updated);
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.all });
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.activities(updated.id) });
    },
  });
}

// ─── Assign referral ────────────────────────────────────────────────────────

export function useAssignReferral() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
      performedBy,
      performedByName,
    }: {
      id: number;
      data: AssignReferralRequest;
      performedBy: string;
      performedByName: string;
    }) => {
      const params = new URLSearchParams({ performedBy, performedByName });
      return apiClient.put<ReferralDto>(
        `/api/referrals/${id}/assign?${params.toString()}`,
        data,
      );
    },
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.referrals.detail(updated.id), updated);
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.all });
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.activities(updated.id) });
    },
  });
}

// ─── Metrics ────────────────────────────────────────────────────────────────

export function useReferralMetrics() {
  return useQuery({
    queryKey: queryKeys.referrals.metrics,
    queryFn: () => apiClient.get<ReferralMetricsDto>('/api/referrals/metrics'),
    staleTime: 30_000,
  });
}

// ─── Comments ───────────────────────────────────────────────────────────────

export function useComments(referralId: number) {
  return useQuery({
    queryKey: queryKeys.referrals.comments(referralId),
    queryFn: () => apiClient.get<ReferralCommentDto[]>(`/api/referrals/${referralId}/comments`),
    enabled: referralId > 0,
  });
}

export function useAddComment() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ referralId, data }: { referralId: number; data: CreateCommentRequest }) =>
      apiClient.post<ReferralCommentDto>(`/api/referrals/${referralId}/comments`, data),
    onSuccess: (_comment, { referralId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.comments(referralId) });
    },
  });
}

// ─── Commissions ─────────────────────────────────────────────────────────────

export function useCommissions(referralId: number) {
  return useQuery({
    queryKey: queryKeys.referrals.commissions(referralId),
    queryFn: () => apiClient.get<CommissionDto[]>(`/api/referrals/${referralId}/commissions`),
    enabled: referralId > 0,
  });
}

export function useMyCommissions(email: string) {
  return useQuery({
    queryKey: queryKeys.referrals.myCommissions(email),
    queryFn: () => apiClient.get<CommissionDto[]>('/api/referrals/commissions/mine', { email }),
    enabled: !!email,
  });
}

export function useUpdateCommissionPayoutStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      commissionId,
      data,
      performedBy,
      performedByName,
    }: {
      commissionId: number;
      referralId: number;
      data: UpdateCommissionPayoutStatusRequest;
      performedBy: string;
      performedByName: string;
    }) => {
      const params = new URLSearchParams({ performedBy, performedByName });
      return apiClient.put<CommissionDto>(
        `/api/referrals/commissions/${commissionId}/payout-status?${params.toString()}`,
        data,
      );
    },
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.commissions(updated.referralId) });
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.activities(updated.referralId) });
      void qc.invalidateQueries({ queryKey: queryKeys.referrals.metrics });
    },
  });
}
